/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { LinearProgress, Tooltip } from "@mui/material";
import { Column } from "data/table.data";
import {
  Dispatch,
  memo,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { PALETTE } from "styles/base/variables";
import { ReactComponent as IconChevronUp } from "assets/img/icon/chevron-sm-up.svg";
import BaseInput from "../Input/Base";
import { useSearchParams } from "react-router-dom";
import { alignToJustify, convertSortParamData } from "utils/converter";
import { isTypingContext } from "utils/dom";
import { MenuType, UpdateTxData } from "models/business.model";
import BaseCheck from "../Check/Base";
import { ExtendedOption } from "../Select/Base";
import { Close, HistoryOutlined, Check as CheckIcon } from "@mui/icons-material";
import IconButton from "../Button/Icon";
import LibraryAddOutlinedIcon from "@mui/icons-material/LibraryAddOutlined";
import { BankStatement } from "models/bank.model";
import { SubjectCodeData } from "models/subject.model";

interface TableProps {
  isLoading?: boolean;
  minWidth?: number | string;
  minHeight?: number | string;
  columns: Column[];
  idField?: string;
  sortLimit?: number;
  dataList: any[];
  subjectList?: SubjectCodeData[];
  highlightRowId?: string;
  holdingRowId?: string;
  selectable?: boolean;
  // 켜지면 링크(linkMaker)/버튼(renderer) 등 셀 자체의 클릭 동작을 모두
  // 무시하고 로우 선택만 동작하도록 만든다. 담당자 일괄 배정처럼 "선택 모드"
  // 동안에는 업체명 이동이나 다운로드 버튼이 눌리면 안 되는 화면에서 사용한다.
  rowSelectOnly?: boolean;
  disableStatus?: {
    input?: boolean | ((data: any) => boolean);
    toggle?: boolean | ((data: any) => boolean);
    openDialog?: boolean;
    cancel?: boolean;
    restore?: boolean;
  };
  menuType?: MenuType;
  checkedSet?: Set<string>;
  setCheckedSet?: Dispatch<SetStateAction<Set<string>>>;
  modifiedMap?: Map<string, UpdateTxData>;
  loadData?: () => void;
  clickRow?: (data: any) => void;
  rowTooltip?: (data: any) => string | undefined;
  onPaste?: (value: string, targetDataIds: string[]) => void;
}

interface TableBodyRowProps {
  index: number;
  data: any;
  idField: string;
  tdPx: number;
  isChecked: boolean;
  activeInput: { dataId: string | null; colId: string | null };
  setActiveInput: Dispatch<
    SetStateAction<{ dataId: string | null; colId: string | null }>
  >;
  columns: Column[];
  selectable?: boolean;
  rowSelectOnly?: boolean;
  disableStatus?: TableProps["disableStatus"];
  modifiedRow?: UpdateTxData;
  subjectList?: SubjectCodeData[];
  menuType?: MenuType;
  highlightRowId?: string;
  holdingRowId?: string;
  clickRow?: (data: any) => void;
  loadData?: () => void;
  onCheckChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCheckClick: (e: React.MouseEvent<HTMLInputElement>, idx: number) => void;
  onRowDragStart?: (index: number, id: string, shiftKey?: boolean) => void;
  onRowDragEnter?: (index: number) => void;
  rowTooltip?: (data: any) => string | undefined;
  selectedColId?: string | null;
  onCellSelect?: (dataId: string, colId: string) => void;
}

const TableBodyRow = memo(function TableBodyRow({
  data,
  index,
  idField,
  tdPx,
  isChecked,
  activeInput,
  setActiveInput,
  columns,
  selectable,
  rowSelectOnly,
  disableStatus,
  modifiedRow,
  subjectList,
  menuType,
  highlightRowId,
  holdingRowId,
  clickRow,
  loadData,
  onCheckChange,
  onCheckClick,
  onRowDragStart,
  onRowDragEnter,
  rowTooltip,
  selectedColId,
  onCellSelect
}: TableBodyRowProps) {
  const activeInputRef = useRef<HTMLInputElement>(null);

  const [options, setOptions] = useState<ExtendedOption[]>([]);

  useEffect(() => {
    if (activeInputRef.current && activeInput.dataId === data[idField]) {
      activeInputRef.current.focus();
    }
  }, [activeInput, data, idField]);

  const resolveDisableStatus = (
    status: boolean | ((rowData: any) => boolean) | undefined
  ) => (typeof status === "function" ? status(data) : Boolean(status));

  const isInputDisabled = resolveDisableStatus(disableStatus?.input);
  const isToggleDisabled = resolveDisableStatus(disableStatus?.toggle);

  const isClickableRow = Boolean(clickRow);
  const isRowDragSelectable =
    selectable && !isClickableRow && Boolean(onRowDragStart);
  const rowTooltipText = rowTooltip?.(data) || "";

  const clickableRowCss = `
        cursor: pointer;
        &:hover td {
          background-color: ${PALETTE.primaryHover};
        }
      `;

  const rowHoverCss = `
        &:hover td {
          background-color: ${PALETTE.primaryHover};
        }
      `;

  const highlightRowCss = `
        background-color: ${PALETTE.primaryHover};
        font-weight: 500;
      `;

  const holdingRowCss = `
        background-color: ${PALETTE.background};
        font-weight: 500;
      `;

  const selectedRowCss = `
        background-color: rgba(122, 75, 255, 0.05);
      `;

  // 로우 선택 전용 모드에서 셀 안의 링크/버튼이 자체 클릭 동작(페이지 이동,
  // 다운로드 등)을 실행하지 못하도록 click/auxclick이 그 대상에 도달하기 전에
  // capture 단계에서 막는다. 체크박스 셀은 자체 클릭 동작이 필요하므로 제외한다.
  const blockRowSelectOnlyClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-row-select-ignore="true"]')) return;
    e.preventDefault();
    e.stopPropagation();
  };

  // 로우 자체에 클릭 동작(clickRow)이 걸려 있을 때, 편집/토글/복사 등 자체 클릭
  // 동작을 가진 셀(data-row-select-ignore="true")을 클릭한 경우에는 로우 클릭이
  // 함께 발동하지 않도록 막는다 (예: 휴대폰 번호 셀 더블클릭 편집 중 상세 다이얼로그가 열리는 것 방지).
  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-row-select-ignore="true"]')) return;
    clickRow?.(data);
  };

  const rowElement = (
    <tr
      css={css`
        border-top: 1px solid ${PALETTE.borderLight};

        td {
          height: 48px;
          padding: 0 ${tdPx}px;

          &:first-of-type {
            border-radius: 0 0 0 8px;
          }
          &:last-child {
            border-radius: 0 0 8px 0;
          }
        }

        ${isClickableRow ? clickableRowCss : rowHoverCss}
        ${selectable && isChecked && selectedRowCss}
        ${highlightRowId && data[idField] === highlightRowId && highlightRowCss}
            ${holdingRowId && data[idField] === holdingRowId && holdingRowCss}
      `}
      onClick={isClickableRow ? handleRowClick : undefined}
      onClickCapture={
        // 로우 선택 전용 모드에서는 셀 안의 링크/버튼이 자체 클릭 동작(페이지 이동,
        // 다운로드 등)을 실행하지 못하도록 click 이벤트가 그 대상에 도달하기 전에
        // capture 단계에서 막는다. 체크박스 셀은 자체 클릭 동작이 필요하므로 제외한다.
        rowSelectOnly ? blockRowSelectOnlyClick : undefined
      }
      onAuxClickCapture={
        // 가운데(휠) 클릭으로 링크를 새 탭에서 여는 것도 막는다 — 이건 click이 아닌
        // auxclick으로 발생해 위 onClickCapture로는 잡히지 않는다.
        rowSelectOnly ? blockRowSelectOnlyClick : undefined
      }
      onMouseDown={
        isRowDragSelectable
          ? (e) => {
              const target = e.target as HTMLElement;
              if (target.closest('[data-row-select-ignore="true"]')) return;
              e.preventDefault();
              onRowDragStart?.(index, data[idField], e.shiftKey);
            }
          : undefined
      }
      onMouseEnter={
        isRowDragSelectable ? () => onRowDragEnter?.(index) : undefined
      }
    >
      {selectable && (
        <td
          data-row-select-ignore="true"
          css={css`
            text-align: center;
          `}
        >
          <BaseCheck
            id={data[idField]}
            checked={isChecked}
            onChange={onCheckChange}
            onClick={(e) => onCheckClick(e, index)}
            css={css`
              cursor: pointer;
            `}
          />
        </td>
      )}
      {columns.map((column, idx) => {
        const colId = column.id;
        const colSx = column.sx;
        const colWidth = colSx?.width;
        const isEditable =
          (column.input && !isInputDisabled) ||
          (column.toggle && !isToggleDisabled);
        const isLink = Boolean(column.linkMaker);
        const isDialog = Boolean(column.openDialog);
        // 로우 선택 전용 모드에서는 셀이 어떤 클릭 동작을 갖고 있든 항상
        // 로우 선택 대상으로 취급한다(=ignore하지 않는다).
        const isRowSelectIgnored = rowSelectOnly
          ? false
          : Boolean(
              column.input ||
                column.toggle ||
                column.openDialog ||
                column.linkMaker ||
                column.cancel ||
                column.copyable
            );
        const tmpBankData = data as BankStatement;
        // 저장된 다중계정 행뿐 아니라, 아직 저장 전인 AI 다중계정 추천(modifiedRow)도
        // 다중계정 행으로 취급해 인라인 편집/셀 선택을 막는다.
        const isMultiAccountCode = Boolean(
          (tmpBankData.multiAccountCodes &&
            tmpBankData.multiAccountCodes.length > 0) ||
            (modifiedRow?.multiAccountCodes &&
              modifiedRow.multiAccountCodes.length > 0)
        );
        const isCellSelectable =
          Boolean(column.copyable) && !isMultiAccountCode && isEditable;
        const isSelectedCell = isCellSelectable && selectedColId === colId;

        let tmpData = { ...data };
        if (loadData) tmpData = { ...data, loadData };

        let isModifiedByBiz = false;
        if (colId === "isVatDeduction")
          isModifiedByBiz = data.lastModifiedBy === "biz";

        let changedVal;
        let isChanged = false;
        const modifiedCurrData = modifiedRow;
        if (modifiedCurrData) {
          const modifiedMulti = modifiedCurrData.multiAccountCodes;
          switch (colId) {
            case "isVatDeduction":
              changedVal = modifiedCurrData[colId as keyof UpdateTxData];
              isChanged = changedVal !== undefined;
              break;
            case "accountCode":
              if (modifiedMulti && modifiedMulti.length > 0) {
                // 저장된 다중계정 행과 동일한 표시 형식: "대표코드 +나머지개수"
                changedVal =
                  modifiedMulti[0].accountCode + " +" + (modifiedMulti.length - 1);
                isChanged = true;
              } else {
                changedVal = modifiedCurrData[colId as keyof UpdateTxData];
                isChanged = changedVal !== undefined;
              }
              break;
            case "accountName":
              if (modifiedMulti && modifiedMulti.length > 0) {
                isChanged = true;
                changedVal = modifiedMulti
                  .map(
                    (acc) =>
                      subjectList?.find((subj) => subj.code === acc.accountCode)
                        ?.name || acc.accountCode
                  )
                  .join(", ");
              } else {
                const modifiedAccCode = modifiedCurrData.accountCode;
                if (modifiedAccCode !== undefined) {
                  isChanged = true;
                  if (subjectList) {
                    changedVal =
                      subjectList.find((subj) => subj.code === modifiedAccCode)
                        ?.name || "";
                  }
                }
              }
              break;
            case "accSplCft":
            case "accVaTxamt":
              const isDdc = modifiedCurrData.isVatDeduction;
              if (isDdc !== undefined) {
                isChanged = true;
                if (colId === "accSplCft")
                  changedVal = isDdc ? data.splCft : data.splCft + data.vaTxamt;
                else if (colId === "accVaTxamt")
                  changedVal = isDdc ? data.vaTxamt : 0;
              }
              break;
          }

          if (isChanged) tmpData[colId] = changedVal;
        }

        const isActiveInput =
          column.input &&
          activeInput.dataId === data[idField] &&
          activeInput.colId === colId;
        const isCancellable = Boolean(column.cancel);
        const defaultVal = colId === "accountCode" ? "" : tmpData[colId];

        const isTooltip = Boolean(column.tooltip);

        const filterOptions = (value: string) => {
          const filtered = column.input?.options?.filter((option) =>
            option.label.includes(value)
          );
          setOptions(filtered || []);
        };

        const submitActiveInput = (tgtValue: string) => {
          if (column.input?.options) {
            if (tgtValue) {
              const firstMatch = column.input.options.find((subject) =>
                subject.label.includes(tgtValue)
              );

              if (firstMatch) {
                column.input.submit(
                  firstMatch.value.toString(),
                  data[idField],
                  colId
                );
              }
            }
          } else {
            const submitVal = column.input?.format
              ? column.input.format(tgtValue)
              : tgtValue;
            column.input?.submit(submitVal, data[idField], colId);
          }
          setActiveInput({ dataId: null, colId: null });
        };

        const keyDownHanlder = (e: React.KeyboardEvent<HTMLInputElement>) => {
          const event = e as React.KeyboardEvent<HTMLInputElement>;
          const eventKey = event.key;
          const eventTarget = event.target as HTMLInputElement;
          const tgtValue = eventTarget?.value;

          if (eventKey === "Enter") {
            submitActiveInput(tgtValue);
          } else if (eventKey === "Escape") {
            setActiveInput({ dataId: null, colId: null });
          }
        };

        const selectOptionHandler = (e: React.FormEvent<HTMLInputElement>) => {
          const event = e as React.FormEvent<HTMLInputElement>;
          const eventTarget = event.target as HTMLInputElement;
          const tgtValue = eventTarget?.value;

          filterOptions(tgtValue);
        };

        const inputChangeHandler = (e: React.FormEvent<HTMLInputElement>) => {
          const eventTarget = e.currentTarget;

          if (column.input?.format) {
            const formatted = column.input.format(eventTarget.value);
            if (formatted !== eventTarget.value) eventTarget.value = formatted;
          }

          if (column.input?.options) selectOptionHandler(e);
        };

        const widthCss = `width: ${colWidth + "px"};`;
        const sxCss = `
              text-align: ${colSx?.textAlign || "left"};
              word-break: ${colSx?.wordBreak || "keep-all"};
              white-space: ${colSx?.whiteSpace || "normal"};
            `;
        const editableCss = `
              cursor: pointer;
              &:hover {
                background-color: ${PALETTE.primaryHover};
              }
              span {
                color: ${PALETTE.textMid};
                font-weight: 700;
              }
            `;
        const changedCss = `
              span {
                background: linear-gradient(to top, yellow 50%,transparent 50%);
              }
            `;
        const changedByBiz = `
              background-color: #d3f9d8;
            `;
        const selectedCellCss = `
              outline: 2px solid ${PALETTE.primary};
              outline-offset: -2px;
            `;
        const linkCss = `
              cursor: pointer;

              a {
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                color: ${PALETTE.textMid};
                font-weight: 700;
                text-decoration: none;
              }

              &:hover {
                a {
                  color: ${PALETTE.primary};
                }
              }
            `;

        const closeButton = () => {
          return (
            <IconButton
              onClick={() => setActiveInput({ dataId: null, colId: null })}
            >
              <Close
                css={css`
                  font-size: 15px;
                `}
              />
            </IconButton>
          );
        };

        const confirmButton = () => {
          return (
            <IconButton
              title="저장"
              onClick={() => submitActiveInput(activeInputRef.current?.value || "")}
            >
              <CheckIcon
                css={css`
                  font-size: 15px;
                `}
              />
            </IconButton>
          );
        };

        const editInputAdornment = () => (
          <div
            css={css`
              display: flex;
              flex-direction: column;
            `}
          >
            {closeButton()}
            {confirmButton()}
          </div>
        );

        return (
          <td
            key={colId + "_" + idx}
            data-row-select-ignore={isRowSelectIgnored ? "true" : undefined}
            css={css`
              position: relative;
              ${colWidth && widthCss}
              ${colSx && sxCss}
                  ${isEditable && !isMultiAccountCode && editableCss}
                  ${isChanged && changedCss}
                  ${isModifiedByBiz && changedByBiz}
                  ${isLink && linkCss}
                  ${isCellSelectable && "cursor: pointer;"}
                  ${isSelectedCell && selectedCellCss}
                  ${isRowDragSelectable &&
                  !isRowSelectIgnored &&
                  "cursor: pointer;"}
            `}
            onClick={
              // rowSelectOnly일 때는 onClickCapture(위)가 이 이벤트에
              // 도달하기 전에 항상 막으므로 별도 분기가 필요 없다.
              isCellSelectable
                ? () => onCellSelect?.(tmpData[idField], colId)
                : undefined
            }
            onDoubleClick={
              // dblclick은 click과 별개 이벤트라 onClickCapture로 막히지
              // 않으므로, rowSelectOnly일 때는 여기서 직접 막아야 한다.
              rowSelectOnly
                ? undefined
                : column.input && !isMultiAccountCode && !isInputDisabled
                  ? () =>
                      setActiveInput({
                        dataId: tmpData[idField],
                        colId: colId
                      })
                  : column.toggle && !isToggleDisabled
                    ? () =>
                        column.toggle?.submit(tmpData[colId], tmpData[idField])
                    : undefined
            }
          >
            {isActiveInput ? (
              <div
                onClick={(e) => e.stopPropagation()}
                css={css`
                  margin-left: -12px;
                  margin-right: -16px;
                  position: relative;
                `}
              >
                <BaseInput
                  ref={activeInputRef}
                  type="text"
                  defaultValue={defaultVal}
                  onKeyDown={keyDownHanlder}
                  onFocus={column.input?.options && selectOptionHandler}
                  onInput={inputChangeHandler}
                  endAdornment={editInputAdornment()}
                  css={css`
                    width: ${column.input?.width || 53}px;
                    padding: 8px;
                  `}
                />
                {options && (
                  <div
                    css={css`
                      width: 175%;
                      max-height: 240px;
                      overflow-y: auto;
                      position: absolute;
                      background-color: ${PALETTE.background};
                      z-index: 100;
                    `}
                  >
                    {options.map((option, optIdx) => (
                      <div
                        key={optIdx}
                        onClick={() => {
                          column.input?.submit(
                            option.value.toString(),
                            tmpData[idField],
                            colId
                          );
                          setActiveInput({ dataId: null, colId: null });
                        }}
                        css={css`
                          padding: 8px 12px;
                          &:hover {
                            background-color: ${PALETTE.primaryHover};
                          }
                        `}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : isTooltip ? (
              <Tooltip
                title={column.tooltip?.(tmpData) || ""}
                placement="top"
                arrow
              >
                <span
                  css={css`
                    ${colSx?.fontSize && `font-size: ${colSx.fontSize + "px"};`}
                    ${colSx?.fontWeight &&
                    `font-weight: ${colSx.fontWeight};
                        `}
                  `}
                >
                  {column.linkMaker?.(tmpData, menuType) ||
                    column.renderer?.(tmpData) ||
                    tmpData[colId] ||
                    "-"}
                </span>
              </Tooltip>
            ) : (
              <span
                css={css`
                  ${colSx?.fontSize && `font-size: ${colSx.fontSize + "px"};`}
                  ${colSx?.fontWeight &&
                  `font-weight: ${colSx.fontWeight};
                        `}
                `}
              >
                {column.linkMaker?.(tmpData, menuType) ||
                  column.renderer?.(tmpData) ||
                  tmpData[colId] ||
                  "-"}
              </span>
            )}
            {isDialog &&
            !isActiveInput &&
            !(isInputDisabled || isToggleDisabled) ? (
              <div
                css={css`
                  position: absolute;
                  top: 4px;
                  right: -10px;
                  height: 100%;
                  display: flex;
                  align-items: center;
                `}
              >
                <IconButton
                  disabled={disableStatus?.openDialog}
                  onClick={(e) => {
                    e.stopPropagation();
                    column.openDialog?.(tmpData);
                  }}
                  css={css`
                    ${disableStatus?.openDialog && "cursor: not-allowed;"}
                  `}
                >
                  <LibraryAddOutlinedIcon
                    css={css`
                      transform: scale(0.75);
                    `}
                  />
                </IconButton>
              </div>
            ) : null}
            {isCancellable ? (
              <div
                css={css`
                  position: absolute;
                  top: 0;
                  right: -6px;
                  display: flex;
                  align-items: center;
                `}
              >
                <IconButton
                  disabled={disableStatus?.cancel}
                  onClick={(e) => {
                    e.stopPropagation();
                    column.cancel?.(tmpData);
                  }}
                  css={css`
                    ${disableStatus?.cancel && "cursor: not-allowed;"}
                  `}
                >
                  <Close
                    css={css`
                      font-size: 15px;
                    `}
                  />
                </IconButton>
              </div>
            ) : null}
            {(isChanged || column.restoreAvailable?.(tmpData)) &&
            isEditable &&
            !isActiveInput &&
            column.restore ? (
              <div
                css={css`
                  position: absolute;
                  top: 0;
                  right: -6px;
                  display: flex;
                  align-items: center;
                `}
              >
                <IconButton
                  title={isChanged ? "되돌리기" : "다시 적용"}
                  disabled={
                    disableStatus?.restore || data[idField] === undefined
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    column.restore?.({
                      dataId: data[idField],
                      colId: colId
                    });
                  }}
                  css={css`
                    ${disableStatus?.restore && "cursor: not-allowed;"}
                  `}
                >
                  <HistoryOutlined
                    css={css`
                      font-size: 15px;
                    `}
                  />
                </IconButton>
              </div>
            ) : null}
          </td>
        );
      })}
    </tr>
  );

  return rowTooltipText ? (
    <Tooltip title={rowTooltipText} placement="top" arrow>
      {rowElement}
    </Tooltip>
  ) : (
    rowElement
  );
});

function Table({
  isLoading,
  minWidth = 1024,
  minHeight = 200,
  columns,
  idField = "id",
  sortLimit = 1,
  dataList,
  subjectList,
  highlightRowId,
  holdingRowId,
  selectable,
  rowSelectOnly,
  disableStatus,
  menuType,
  checkedSet = new Set(),
  setCheckedSet,
  modifiedMap,
  loadData,
  clickRow,
  rowTooltip,
  onPaste
}: TableProps) {
  const tdPx = 10;

  const [searchParams, setSearchParams] = useSearchParams();

  const [isCheckAll, setIsCheckAll] = useState<boolean>(false);
  const [activeInput, setActiveInput] = useState<{
    dataId: string | null;
    colId: string | null;
  }>({
    dataId: null,
    colId: null
  });
  // 체크박스 렌더링과 무관한 내부 북마크 값이라 ref로만 관리한다.
  // state로 두면 값이 바뀔 때마다 이 값에 의존하는 콜백(clickHandler 등)이
  // 새 레퍼런스가 되어 TableBodyRow의 memo가 매번 깨진다.
  const lastShiftedIndexRef = useRef<number | null>(null);

  // checkedSet은 prop이라 ref로 대체할 수 없으니, 콜백이 최신 값을
  // "읽기"만 하도록 별도 ref에 미러링해 deps에서 빼낸다.
  const checkedSetRef = useRef(checkedSet);
  useEffect(() => {
    checkedSetRef.current = checkedSet;
  }, [checkedSet]);

  // 계정코드/공제여부처럼 copyable 컬럼의 "선택된 셀" — 행 체크박스 선택과는
  // 별개로, 복사(Ctrl+C)/붙여넣기(Ctrl+V) 대상을 가리킨다.
  const [selectedCell, setSelectedCell] = useState<{
    dataId: string | null;
    colId: string | null;
  }>({ dataId: null, colId: null });

  const selectCell = useCallback((dataId: string, colId: string) => {
    setSelectedCell({ dataId, colId });
  }, []);

  useEffect(() => {
    // size만 비교하면 checkedSet에 현재 dataList 밖의(필터로 가려진) id가
    // 섞여 있을 때 우연히 개수가 같아져 잘못 체크됨으로 표시될 수 있다.
    if (
      dataList.length &&
      dataList.every((d) => checkedSet.has(d[idField]))
    )
      setIsCheckAll(true);
    else setIsCheckAll(false);
  }, [checkedSet, dataList, idField]);

  const checkHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { id, checked } = e.target;
      setSelectedCell({ dataId: null, colId: null });
      setCheckedSet?.((prev) => {
        const next = new Set(prev);
        if (checked) next.add(id);
        else next.delete(id);
        return next;
      });
    },
    [setCheckedSet]
  );

  const clickHandler = useCallback(
    (e: React.MouseEvent<HTMLInputElement>, idx: number) => {
      const { checked } = e.target as HTMLInputElement;
      const lastShiftedIndex = lastShiftedIndexRef.current;
      setSelectedCell({ dataId: null, colId: null });

      if (e.shiftKey && lastShiftedIndex !== null) {
        setCheckedSet?.((prev) => {
          const next = new Set(prev);
          const start = Math.max(0, Math.min(idx, lastShiftedIndex));
          const end = Math.min(
            dataList.length - 1,
            Math.max(idx, lastShiftedIndex)
          );
          for (let i = start; i <= end; i++) {
            next.add(dataList[i][idField]);
          }
          return next;
        });
      }

      lastShiftedIndexRef.current = checked ? idx : null;
    },
    [dataList, idField, setCheckedSet]
  );

  const checkAllHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target;
      setSelectedCell({ dataId: null, colId: null });
      // dataList 밖의(필터로 가려진) 기존 선택은 건드리지 않고, 현재 보이는
      // 행만 전체 선택/해제한다.
      setCheckedSet?.((prev) => {
        const next = new Set(prev);
        dataList.forEach((d) => {
          if (checked) next.add(d[idField]);
          else next.delete(d[idField]);
        });
        return next;
      });
    },
    [dataList, idField, setCheckedSet]
  );

  // 행 드래그 선택: mousedown한 행의 체크 상태를 반전한 값을 목표값으로 삼아
  // 드래그가 지나간 모든 행에 동일하게 적용한다(엑셀/탐색기식 드래그 선택).
  const dragSelectRef = useRef<{ startIndex: number; checked: boolean } | null>(
    null
  );

  const applyDragSelection = useCallback(
    (toIndex: number) => {
      const dragState = dragSelectRef.current;
      if (!dragState) return;
      const { startIndex, checked } = dragState;
      const start = Math.min(startIndex, toIndex);
      const end = Math.max(startIndex, toIndex);
      setCheckedSet?.((prev) => {
        const next = new Set(prev);
        for (let i = start; i <= end; i++) {
          const id = dataList[i][idField];
          if (checked) next.add(id);
          else next.delete(id);
        }
        return next;
      });
    },
    [dataList, idField, setCheckedSet]
  );

  const rowDragStartHandler = useCallback(
    (index: number, id: string, shiftKey?: boolean) => {
      setSelectedCell({ dataId: null, colId: null });

      const lastShiftedIndex = lastShiftedIndexRef.current;
      if (shiftKey && lastShiftedIndex !== null) {
        // 체크박스 shift-클릭과 동일하게, 행 본문을 shift-클릭해도 범위 선택되도록 한다.
        const start = Math.max(0, Math.min(index, lastShiftedIndex));
        const end = Math.min(
          dataList.length - 1,
          Math.max(index, lastShiftedIndex)
        );
        setCheckedSet?.((prev) => {
          const next = new Set(prev);
          for (let i = start; i <= end; i++) next.add(dataList[i][idField]);
          return next;
        });
        lastShiftedIndexRef.current = index;
        return;
      }

      dragSelectRef.current = {
        startIndex: index,
        checked: !checkedSetRef.current.has(id)
      };
      lastShiftedIndexRef.current = index;
      applyDragSelection(index);
    },
    [applyDragSelection, dataList, idField, setCheckedSet]
  );

  const rowDragEnterHandler = useCallback(
    (index: number) => {
      if (!dragSelectRef.current) return;
      applyDragSelection(index);
    },
    [applyDragSelection]
  );

  useEffect(() => {
    const stopDragSelect = () => {
      dragSelectRef.current = null;
    };
    window.addEventListener("mouseup", stopDragSelect);
    return () => window.removeEventListener("mouseup", stopDragSelect);
  }, []);

  // 선택된 셀(Ctrl+C) 또는 선택된 셀/체크된 행(Ctrl+V)에 대한 복사·붙여넣기.
  // 텍스트 입력 중(검색창, 편집중인 셀 등)에는 브라우저 기본 동작을 그대로 둔다.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const modifierPressed = e.ctrlKey || e.metaKey;
      if (!modifierPressed) return;

      const key = e.key.toLowerCase();
      if (key !== "c" && key !== "v") return;

      if (isTypingContext()) return;

      if (key === "c") {
        // 페이지의 다른 텍스트를 실제로 드래그해 선택한 상태라면, 그건
        // 우리 셀 선택보다 우선해서 브라우저 기본 복사 동작을 살려둔다.
        if (window.getSelection()?.toString()) return;
        if (!selectedCell.dataId || !selectedCell.colId) return;
        const column = columns.find((col) => col.id === selectedCell.colId);
        if (!column?.copyValue) return;
        const rowData = dataList.find(
          (d) => d[idField] === selectedCell.dataId
        );
        if (!rowData) return;

        // 저장 전 임시 수정값이 있으면(노란 표시 중인 값) 화면에 보이는 그
        // 값을 복사한다 — dataList의 원본값을 그대로 복사하면 안 된다.
        const modifiedRow = modifiedMap?.get(selectedCell.dataId) as
          | Record<string, unknown>
          | undefined;
        const overriddenValue = modifiedRow?.[selectedCell.colId];
        const dataForCopy =
          overriddenValue !== undefined
            ? { ...rowData, [selectedCell.colId]: overriddenValue }
            : rowData;

        const text = column.copyValue(dataForCopy);
        e.preventDefault();
        navigator.clipboard?.writeText(text).catch(() => {});
        return;
      }

      if (!onPaste) return;
      const targetIds = selectedCell.dataId
        ? [selectedCell.dataId]
        : Array.from(checkedSetRef.current);
      if (targetIds.length === 0) return;

      e.preventDefault();
      navigator.clipboard
        ?.readText()
        .then((text) => onPaste(text, targetIds))
        .catch(() => {});
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedCell, columns, dataList, idField, modifiedMap, onPaste]);

  const sortHandler = (colId: string) => {
    const newSortData = convertSortParamData(searchParams.get("sort"), colId);
    let newSortList = Object.entries(newSortData);
    if (newSortList.length > sortLimit) {
      newSortList = newSortList.slice(1);
    }

    const newSortString = newSortList.map(([k, v]) => `${k},${v}`).join("|");
    searchParams.set("sort", newSortString);
    setSearchParams(searchParams);
  };

  return (
    <div
      css={css`
        max-height: calc(100vh - 360px);
        min-height: ${typeof minHeight === "number"
          ? minHeight + "px"
          : minHeight};
        border: 1px solid ${PALETTE.border};
        border-radius: 8px;
        overflow-x: auto;
        position: relative;
      `}
    >
      {isLoading && (
        <div
          css={css`
            width: 100%;
            position: absolute;
            top: -1px;
            z-index: 100;
          `}
        >
          <LinearProgress
            sx={{
              backgroundColor: PALETTE.primaryBackground,
              "& .MuiLinearProgress-bar": {
                backgroundColor: PALETTE.primary
              }
            }}
          />
        </div>
      )}
      <table
        css={css`
          width: 100%;
          min-width: ${typeof minWidth === "number"
            ? minWidth + "px"
            : minWidth};
          position: relative;
          overflow-x: hidden;
          border-collapse: collapse;
          font-size: 15px;
        `}
      >
        <thead
          css={css`
            background-color: ${PALETTE.background};
            top: 0;
            position: sticky;
            z-index: 10;
          `}
        >
          <tr
            css={css`
              th {
                height: 40px;
                padding: 0 ${tdPx}px;
                font-weight: 500;
                word-break: keep-all;

                &:first-of-type {
                  border-radius: 0 0 0 8px;
                }
                &:last-child {
                  border-radius: 0 0 8px 0;
                }
              }
            `}
          >
            {selectable && (
              <th
                css={css`
                  width: 24px;
                `}
              >
                <BaseCheck
                  id="all"
                  checked={isCheckAll}
                  onChange={checkAllHandler}
                  css={css`
                    cursor: pointer;
                  `}
                />
              </th>
            )}
            {columns.map((column, idx) => {
              const colId = column.id;
              const colSx = column.sx;
              const colWidth = colSx?.width;
              const widthCss = `
                width: ${colWidth + "px"};
                min-width: ${colWidth + "px"};
                max-width: ${colWidth + "px"};
              `;

              return (
                <th
                  key={colId + "_" + idx}
                  onClick={
                    column.sorting ? () => sortHandler(colId) : undefined
                  }
                  css={css`
                    ${colWidth && widthCss}
                    cursor: ${column.sorting ? "pointer" : "default"};
                  `}
                >
                  <span
                    css={css`
                      display: flex;
                      align-items: center;
                      justify-content: ${alignToJustify(colSx?.textAlign)};
                      column-gap: 4px;
                    `}
                  >
                    {column.label}
                    {convertSortParamData(
                      searchParams.get("sort"),
                      colId,
                      true
                    )[colId] ? (
                      <IconChevronUp
                        css={css`
                          transform: ${convertSortParamData(
                            searchParams.get("sort"),
                            colId
                          )[colId] === "desc"
                            ? "rotate(0deg)"
                            : "rotate(180deg)"};
                        `}
                      />
                    ) : (
                      column.sorting && (
                        <div
                          css={css`
                            margin-left: 2px;
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                          `}
                        >
                          <IconChevronUp
                            css={css`
                              width: 12px;
                              transform: translateY(4px);
                            `}
                          />
                          <IconChevronUp
                            css={css`
                              width: 12px;
                              transform: translateY(-4px) rotate(180deg);
                            `}
                          />
                        </div>
                      )
                    )}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody
          css={css`
            background-color: ${PALETTE.light};
          `}
        >
          {dataList.length ? (
            dataList.map((data, index) => (
              <TableBodyRow
                key={String(data[idField])}
                index={index}
                data={data}
                idField={idField}
                tdPx={tdPx}
                isChecked={checkedSet.has(data[idField])}
                modifiedRow={modifiedMap?.get(data[idField])}
                activeInput={activeInput}
                setActiveInput={setActiveInput}
                columns={columns}
                selectable={selectable}
                rowSelectOnly={rowSelectOnly}
                disableStatus={disableStatus}
                subjectList={subjectList}
                menuType={menuType}
                highlightRowId={highlightRowId}
                holdingRowId={holdingRowId}
                clickRow={clickRow}
                loadData={loadData}
                onCheckChange={checkHandler}
                onCheckClick={clickHandler}
                onRowDragStart={rowDragStartHandler}
                onRowDragEnter={rowDragEnterHandler}
                rowTooltip={rowTooltip}
                selectedColId={
                  selectedCell.dataId === data[idField]
                    ? selectedCell.colId
                    : null
                }
                onCellSelect={selectCell}
              />
            ))
          ) : (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                css={css`
                  border-top: 1px solid ${PALETTE.border};
                  text-align: center;
                  font-size: 16px;
                  font-weight: 500;
                `}
              >
                <div
                  css={css`
                    height: 100%;
                    min-height: 160px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                  `}
                >
                  {isLoading
                    ? "데이터를 확인하는 중입니다."
                    : "데이터가 없습니다."}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default Table;
